import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import useEntity from 'hooks/useEntity';

import { UIVersionContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import useTabs from 'screens/EntityDetailsShow/SelectedEntityProvider/useTabs';
import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import {
  V3AppsContext,
  V3SelectedAppContext,
} from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';

import NavItem from '../../NavItem';

const V3EntityNavItem = ({
  item,
  entity,
}: {
  item: V3.Navigation.MappedEntityView | V3.Navigation.EntityApp;
  entity: EntityDetails.Entity;
}) => {
  const { uiVersion } = useContext(UIVersionContext);
  const { availableProductApps } = useContext(V3AppsContext);
  const { selectedProductApp, selectedProduct, selectedWorkspace } =
    useContext(V3SelectedAppContext);
  const { entityDetailsApps } = useContext(EntityDefinitionsContext);
  const entityApp = useMemo(() => {
    return entityDetailsApps.find((a) => a.id === item.entityAppId);
  }, [item.entityAppId, entityDetailsApps]);
  const { tabs } = useTabs({ app: entityApp });

  const { pathname } = useLocation();
  const getIsActive = useCallback(() => {
    if (uiVersion === 'v3') {
      return undefined;
    }

    if (entityApp) {
      return pathname.includes(`entity/${entityApp.urlSlug}`);
    }

    return false;
  }, [uiVersion, entityApp, pathname]);
  const [isActive, setIsActive] = useState<boolean | undefined>(() =>
    getIsActive(),
  );
  useEffect(() => {
    setIsActive(getIsActive());
  }, [getIsActive]);

  const to = useMemo(() => {
    if (tabs.length === 0) {
      return '/';
    }
    const firstTab = tabs[0].typeId;

    if (selectedWorkspace) {
      return `/workspaces/${selectedWorkspace.urlSlug}/${item.key}/${firstTab}`;
    }

    if (!selectedProduct || !selectedProductApp) {
      return '/';
    }

    const subAppsThisUserHasAccessTo = availableProductApps.filter((app) =>
      selectedProduct.appIds.includes(app.id),
    );

    if (subAppsThisUserHasAccessTo.length === 0) {
      return '/';
    }

    if (subAppsThisUserHasAccessTo.length === 1) {
      return `/apps/${selectedProduct.urlSlug}/${item.key}/${firstTab}`;
    }

    return `/apps/${selectedProduct.urlSlug}/${selectedProductApp.urlSlug}/${item.key}/${firstTab}`;
  }, [
    availableProductApps,
    item.key,
    selectedProduct,
    selectedProductApp,
    selectedWorkspace,
    tabs,
  ]);

  return (
    <NavItem
      testId={`${item.label}-ready`}
      to={to}
      isActiveOverride={isActive}
      label={item.label}
      icon={item.icon}
    />
  );
};

const Gate = ({
  item,
}: {
  item: V3.Navigation.MappedEntityView | V3.Navigation.EntityApp;
}) => {
  const { entity } = useEntity({
    entityId: item.entityId,
    entityAppId: item.entityAppId,
  });

  if (!entity) {
    return null;
  }

  return <V3EntityNavItem item={item} entity={entity} />;
};

export default Gate;
